import { UrlError } from '@utils/errors';
import { useErrorLogger } from '@composables/useErrorLogger';

export interface UsePostMessageReturn {
    url: ComputedRef<URL | undefined>;
    decodedEventUrl: ComputedRef<string>;
    postMessageToParentFrame: <T>(eventName: string, data: T) => void;
}

export const usePostMessage = (): UsePostMessageReturn => {
    const { query } = useRoute();
    const { errorLoggerCaptureException } = useErrorLogger();

    const decodedEventUrl = computed(() => {
        try {
            if (!query.event_url) {
                console.warn('Required event URL is missing');
                errorLoggerCaptureException(
                    new UrlError('Required event URL is missing')
                );
                return '';
            }
            return atob((query.event_url as string) || '');
        } catch (error: unknown) {
            console.warn('An error occured during URL decoding');
            errorLoggerCaptureException(error, {
                extra: {
                    url: query.event_url,
                },
            });
            return '';
        }
    });

    const url = computed(() => {
        const decodedUrlValue = unref(decodedEventUrl);
        try {
            if (!decodedUrlValue) {
                return;
            }
            return new URL(decodedUrlValue);
        } catch (error: unknown) {
            console.warn('An error occured during URL parsing');
            errorLoggerCaptureException(error, {
                extra: {
                    url: decodedUrlValue,
                },
            });
        }
    });

    const postMessageToParentFrame = <T>(eventName: string, data: T) => {
        if (!decodedEventUrl.value) {
            console.warn('Required event URL is missing');
            return;
        }

        window.parent.postMessage(
            JSON.stringify({
                eventName,
                data,
            }),
            decodedEventUrl.value
        );
    };

    return { postMessageToParentFrame, decodedEventUrl, url };
};
